import { RefreshCw, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { DatePickerV3 } from "../../../components/date-picker-v3";
import { SelectV2, type SelectV2Option } from "../../../components/select-v2";
import { Input } from "../../../components/input";
import { getTimeRangeFilter } from "../../../lib/date-time";
import { useGetAlertTypeOptions } from "../../common/public-options";
import type { NotificationFilter } from "../types/notification.types";
import type { PublicOptionResponse } from "../../common/public-options/publicOptions.api";
import type { Dispatch, SetStateAction } from "react";

const TIME_FILTER_OPTIONS = [
  "All time",
  "Today",
  "This week",
  "This Month",
  "Last Year",
  "Custom Range",
] as const;

type NotificationTimeFilter = (typeof TIME_FILTER_OPTIONS)[number];

type NotificationToolbarProps = {
  filters: NotificationFilter;
  setFilters: Dispatch<SetStateAction<NotificationFilter>>;
  loading: boolean;
  onRefresh: () => void;
};

export function NotificationToolbar({
  filters,
  setFilters,
  loading,
  onRefresh,
}: NotificationToolbarProps) {
  const { t } = useTranslation();
  const {
    alertTypes,
    loading: alertTypesLoading,
    error: alertTypesError,
  } = useGetAlertTypeOptions();
  const [timeFilter, setTimeFilter] = useState<NotificationTimeFilter>("All time");
  const [pinFilter, setPinFilter] = useState("All Alerts");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  const ONE_DAY_MS = 24 * 60 * 60 * 1000;

  const updateNotificationDateFilter = (nextStartDate?: Date, nextEndDate?: Date) => {
    setFilters((current) => {
      if (!nextStartDate || !nextEndDate) {
        const { notification_date, ...restFilters } = current;
        return restFilters;
      }

      const timeRangeFilter = getTimeRangeFilter("Custom Range", nextStartDate, nextEndDate);
      if (!timeRangeFilter?.from || !timeRangeFilter?.to) {
        const { notification_date, ...restFilters } = current;
        return restFilters;
      }

      return {
        ...current,
        notification_date: {
          from: timeRangeFilter.from,
          to: timeRangeFilter.to,
        },
      };
    });
  };

  const setDateRange = (
    nextTimeFilter: NotificationTimeFilter,
    nextStartDate?: Date,
    nextEndDate?: Date,
  ) => {
    setTimeFilter(nextTimeFilter);
    setStartDate(nextStartDate);
    setEndDate(nextEndDate);
    updateNotificationDateFilter(nextStartDate, nextEndDate);
  };

  const alertTypeOptions = useMemo<SelectV2Option<PublicOptionResponse>[]>(
    () =>
      alertTypes.map((alertType): SelectV2Option<PublicOptionResponse> => ({
        value: alertType.value,
        textValue: alertType.label,
        label: (
          <span className="flex items-center gap-2">
            <span className="inline-flex h-5 w-5 items-center justify-center text-base leading-none">
              {alertType.icon_url || alertType.icon || ""}
            </span>
            <span className="font-medium">{alertType.label}</span>
          </span>
        ),
      })),
    [alertTypes],
  );

  const handleSearchChange = (value: string) => {
    setFilters((current) => {
      if (!value.trim()) {
        const { keyword, ...restFilters } = current;
        return restFilters;
      }

      return { ...current, keyword: value };
    });
  };

  const handleCategoryFilterChange = (value: string) => {
    setFilters((current) => {
      if (!value) {
        const { alert_type_id, ...restFilters } = current;
        return restFilters;
      }

      return {
        ...current,
        alert_type_id: value,
      };
    });
  };

  const handlePinFilterChange = (value: string) => {
    setPinFilter(value);

    if (value === "All Alerts") {
      setFilters((current) => {
        const { pin_status, ...restFilters } = current;
        return restFilters;
      });
      return;
    }

    setFilters((current) => ({
      ...current,
      pin_status: value === "Pinned" ? "pinned" : "unpinned",
    }));
  };

  const handleTimeFilterChange = (value: string) => {
    if (!TIME_FILTER_OPTIONS.includes(value as NotificationTimeFilter)) {
      return;
    }

    const nextValue = value as NotificationTimeFilter;

    if (nextValue === "All time") {
      setDateRange(nextValue, undefined, undefined);
      return;
    }

    if (nextValue === "Custom Range") {
      setTimeFilter("Custom Range");
      return;
    }

    const presetRange = getTimeRangeFilter(nextValue);
    if (presetRange?.from && presetRange?.to) {
      const nextStartDate = new Date(presetRange.from);
      const nextEndDate = new Date(presetRange.to);
      setDateRange(nextValue, nextStartDate, nextEndDate);
    }
  };

  const handleClearDateRange = () => {
    setDateRange("All time", undefined, undefined);
  };

  const handleRefresh = () => {
    onRefresh();
  };

  const handleClearFilters = () => {
    setFilters({});
    setTimeFilter("All time");
    setPinFilter("All Alerts");
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const hasAnyActiveFilter =
    Boolean(filters.keyword?.trim()) ||
    Boolean(filters.alert_type_id) ||
    Boolean(filters.pin_status) ||
    Boolean(filters.notification_date) ||
    Boolean(filters.title) ||
    Boolean(filters.content) ||
    timeFilter !== "All time" ||
    Boolean(startDate) ||
    Boolean(endDate) ||
    pinFilter !== "All Alerts";

  const activeFilterCount = [
    filters.keyword?.trim(),
    filters.alert_type_id,
    filters.pin_status,
    filters.notification_date,
    filters.title,
    filters.content,
  ].filter(Boolean).length;

  const showDateRange = timeFilter !== "All time" || Boolean(startDate) || Boolean(endDate);
  const dateDisplayFormat = timeFilter === "Today" ? "MM/dd/yyyy HH:mm" : "MM/dd/yyyy";

  const handleStartDateChange = (date: Date | undefined) => {
    setTimeFilter("Custom Range");
    setStartDate(date);
    updateNotificationDateFilter(date, endDate);
  };

  const handleEndDateChange = (date: Date | undefined) => {
    setTimeFilter("Custom Range");
    setEndDate(date);
    updateNotificationDateFilter(startDate, date);
  };

  return (
    <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between w-100">
      <div className="flex flex-1 flex-row flex-wrap justify-start items-center gap-4">
        <div className="w-full sm:w-auto">
          <Input
            startIcon={<Search className="h-4 w-4 text-gray-400" />}
            placeholder="Search alerts..."
            value={filters.keyword ?? ""}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full sm:w-64"
          />
        </div>

        <SelectV2
          value={filters.alert_type_id ?? ""}
          onChange={handleCategoryFilterChange}
          options={alertTypeOptions}
          placeholder="Alert Type"
          ariaLabel="Alert Type"
          className="w-full sm:w-[300px]"
          disabled={alertTypesLoading || Boolean(alertTypesError)}
          clearable={Boolean(filters.alert_type_id)}
          onClear={() => handleCategoryFilterChange("")}
          triggerClassName="min-w-[12rem] text-left"
        />

        <SelectV2
          value={pinFilter === "All Alerts" ? "" : pinFilter}
          onChange={handlePinFilterChange}
          options={[
            { value: "Pinned", label: "Pinned" },
            { value: "Unpinned", label: "Unpinned" },
          ]}
          placeholder="All Alerts"
          ariaLabel="Pin filter"
          clearable={Boolean(filters.pin_status)}
          onClear={() => handlePinFilterChange("All Alerts")}
          className="w-full sm:w-[300px]"
          triggerClassName="min-w-[10rem] text-left"
        />

        {!showDateRange ? (
          <SelectV2
            value={timeFilter}
            onChange={handleTimeFilterChange}
            className="w-full sm:w-[300px]"
            options={[
              { value: "All time", label: "All time" },
              { value: "Today", label: "Today" },
              { value: "This week", label: "This week" },
              { value: "This Month", label: "This Month" },
              { value: "Last Year", label: "Last Year" },
              { value: "Custom Range", label: "Custom Range" },
            ]}
            placeholder="All time"
            ariaLabel="Time filter"
            triggerClassName="min-w-[10rem] text-left"
          />
        ) : (
          <div className="flex items-center gap-2">
            <DatePickerV3
              value={startDate}
              onChange={handleStartDateChange}
              placeholder="Start date"
              primaryColor="#10b981"
              className="h-9 w-40"
              dateFormat={dateDisplayFormat}
              ariaLabel="Start date"
              disableFromDate={endDate ? new Date(endDate.getTime() + ONE_DAY_MS) : undefined}
            />

            <span className="text-gray-500">to</span>

            <DatePickerV3
              value={endDate}
              onChange={handleEndDateChange}
              placeholder="End date"
              primaryColor="#10b981"
              className="h-9 w-40"
              dateFormat={dateDisplayFormat}
              ariaLabel="End date"
              disableToDate={startDate ? new Date(startDate.getTime() - ONE_DAY_MS) : undefined}
            />

            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearDateRange}
              className="h-8 w-8 p-2 text-gray-500 hover:text-gray-700"
            >
              <span aria-hidden="true">×</span>
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {hasAnyActiveFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            {t("common.clear")}
            {activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          {t("ui.refresh")}
        </Button>
      </div>
    </div>
  );
}
